import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@/shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, Trash2Icon, Loader2Icon } from 'lucide-react';
import type { PlanProduct } from '../types/plan.types';

interface Props {
    data: PlanProduct[];
    productId: string;
    productName: string;
    onRemove: (planId: string) => void;
    removingPlanId?: string | null;
    isLoading?: boolean;
}

export const ProductPlanDataTable: React.FC<Props> = ({
    data,
    productId,
    productName,
    onRemove,
    removingPlanId = null,
    isLoading = false,
}) => {
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<PlanProduct>[]>(() => [
        {
            accessorKey: 'name',
            header: 'Plan Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'description',
            header: 'Plan Description',
            enableSorting: false,
            cell: (info) => {
                const description = info.getValue() as string | null | undefined;
                return description
                    ? <span className="text-sm text-mp-foreground">{description}</span>
                    : <span className="text-sm text-mp-muted">&mdash;</span>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const planId = info.row.original.id;
                const isRemoving = planId === removingPlanId;

                if (isRemoving) {
                    return <Loader2Icon size={18} className="animate-spin text-mp-muted" />;
                }

                return (
                    <div className="flex space-x-2">
                        <Tooltip content="View plan">
                            <TextLink
                                color="primary"
                                onClick={() =>
                                    navigate(`/smr-admin/plans/${planId}`, {
                                        state: {
                                            backTo: `/smr-admin/products/${productId}?tab=plans`,
                                            backLabel: productName,
                                        },
                                    })
                                }
                                ariaLabel="View plan"
                                startIcon={<EyeIcon size={20} />}
                            />
                        </Tooltip>
                        <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                            <Tooltip content="Remove plan">
                                <TextLink
                                    color="error"
                                    onClick={() => onRemove(planId)}
                                    disabled={removingPlanId !== null}
                                    ariaLabel="Remove plan"
                                    startIcon={<Trash2Icon size={18} />}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [navigate, onRemove, removingPlanId, productId, productName]);

    if (isLoading) {
        return <LoadingSpinner size="lg" className="py-8" message="Loading plans..." />;
    }

    return <Table<PlanProduct> data={data} columns={columns} enableSorting={false} />;
};
